import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { AuthModel } from '../model/auth.model';
import { SignUserModel } from '../model/sign-user-model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { AnalyticsService } from '../modules/google-analytics/service/analytics.service';
import { UserData } from '../modules/google-analytics/model/user-data.model';
import { DateTimeUtil } from '../util/date-time-util';
import { SurvicateService, SurvicateUser } from './survicate.service';
import { ApiUrlUtil } from '../util/api-url-util';
import { AuthUtilService } from './auth-util.service';
import { UserTypes } from '../enum/user-types';
import { SuiteUserAuth } from '../model/suite-user-auth';
import { ActivatedRoute } from '@angular/router';

const CONTROLLER = 'authuser';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public authModel = new BehaviorSubject<AuthModel>(undefined);

  private errorId = '';
  private _accessKey = '';
  private suiteAuthUrl: string;
  private _signUserModel: SignUserModel;

  constructor(
    private authUtilService: AuthUtilService,
    private http: HttpClient,
    private googleAnalyticsService: AnalyticsService,
    private survicateService: SurvicateService,
    private route: ActivatedRoute
  ) {
    this.suiteAuthUrl = ApiUrlUtil.getFormattedSuiteUrl();
  }

  get signUserModel(): SignUserModel {
    return this._signUserModel;
  }

  get accessKey(): string {
    return this._accessKey;
  }

  set accessKey(value: string) {
    this._accessKey = value;
  }

  init() { }

  getProfile(): UserTypes {
    return this._signUserModel.type ?? UserTypes.User;
  }

  getUser(id: any): Observable<any> {
    return this.http.get<any>(`${this.suiteAuthUrl}/users/${id}`);
  }

  public getAuth(): Observable<BehaviorSubject<AuthModel>> {
    if (!this.signUserModel && !this.checkAccessKey()) {
      const timezone = DateTimeUtil.getCurrentTimezone();

      return this.http
        .post<AuthModel>(CONTROLLER.concat('/login'), { timezone })
        .pipe(
          map((authModel: AuthModel) => {
            this._signUserModel = authModel.signUser;
            this.authModel.next(authModel);
            this.authUtilService.setUser(authModel.suiteUser);
            this.authUtilService.setUserSign(authModel.signUser);
            this.googleAnalyticsService.setUserData(
              this.getUserDataForAnalytics(this._signUserModel)
            );
            this.survicateService.setUser(
              new SurvicateUser(this._signUserModel.mail)
            );
            return this.authModel;
          })
        );
    }
    return of(this.authModel);
  }

  refreshToken() {
    return this.http
      .post<SuiteUserAuth>(
        `${this.suiteAuthUrl}/users/refresh-token`,
        {},
        { withCredentials: true }
      )
      .pipe(
        map((user: SuiteUserAuth) => {
          this.startRefreshTokenTimer();
          return user;
        })
      );
  }

  revokeToken() {
    this.http
      .post<any>(
        `${this.suiteAuthUrl}/users/revoke-token`,
        {},
        { withCredentials: true }
      )
      .subscribe();
  }

  public getValidateByAccessKey(
    accessKey: string,
    accessToken: string,
    batch: boolean
  ) {
    if (accessKey !== undefined) {
      this._accessKey = accessKey;
    }
    const timezone = DateTimeUtil.getCurrentTimezone();

    return this.http
      .post(CONTROLLER.concat('/validateByAccessKey'), {
        accessKey,
        accessToken,
        batch,
        timezone,
      })
      .pipe(
        map((authModel: AuthModel) => {
          this._accessKey = '';
          this._signUserModel = authModel.signUser;
          this.authModel.next(authModel);
          this.googleAnalyticsService.setUserData(
            this.getUserDataForAnalytics(authModel.signUser)
          );
          return authModel;
        })
      );
  }

  public setErrorId(errorId: string) {
    this.errorId = errorId;
  }

  public getErrorId() {
    return this.errorId;
  }

  private getUserDataForAnalytics(user: SignUserModel): UserData {
    if (user) {
      return new UserData(user.id, user.name, user.type);
    }
    return null;
  }

  private refreshTokenTimeout;

  private startRefreshTokenTimer() {
    // Transforma o token JWT em um objeto json para utilizar a info de expiração
    const jwtToken = JSON.parse(
      atob(this.authUtilService.getJwtToken().split('.')[1])
    );

    // Configura um timeout para a renovação do token 1 minuto antes de sua expiração
    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - 60 * 1000;
    this.refreshTokenTimeout = setTimeout(
      () => this.refreshToken().subscribe(),
      timeout
    );
  }

  private checkAccessKey() {
    var queryString = window.location.hash;
    return queryString.includes('accessKey');
  }

  stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }
}
